/* PVA native collection adapter 0.1.0 — read-only, complete pages, no credentials.
 * Transport is provided by sections/pva-catalog.liquid and product.pva-variants.
 * A partial or inconsistent catalog is never returned as a complete result.
 */
(function (global) {
  'use strict';
  class DataError extends Error { constructor(message, code) { super(message); this.name='PVADataError'; this.code=code; } }
  function assert(ok, message, code='DATA_INCOMPLETE') { if (!ok) throw new DataError(message,code); }
  function localURL(path, base) { const u=new URL(path,base); assert(u.origin===new URL(base).origin,'Unzulässige Datenadresse.','ORIGIN'); return u; }
  function parseHTML(text, selector) {
    const doc=new DOMParser().parseFromString(text,'text/html');
    const nodes=doc.querySelectorAll(selector); assert(nodes.length===1,'Die Datenansicht fehlt oder ist mehrfach vorhanden.','TEMPLATE');
    try { return JSON.parse(nodes[0].textContent); } catch { throw new DataError('Die Datenansicht enthält kein gültiges JSON.','JSON'); }
  }
  async function pool(tasks, concurrency) {
    const output=new Array(tasks.length); let index=0;
    await Promise.all(Array.from({length:Math.min(concurrency,tasks.length)}, async()=>{
      for(;;) { const i=index++; if(i>=tasks.length) return; output[i]=await tasks[i](); }
    })); return output;
  }
  class CatalogLoader {
    constructor(config) { this.config=config; this.base=global.location.href; this.cache=new Map(); this.abortController=new AbortController(); }
    stop() { this.abortController.abort(); this.cache.clear(); }
    contextURL(path) {
      const u=localURL(path,this.base), here=new URL(this.base);
      ['preview_theme_id','_fd','pb'].forEach(k=>{if(here.searchParams.has(k))u.searchParams.set(k,here.searchParams.get(k));});
      return u;
    }
    async fetchPage(url, selector) {
      const u=localURL(url,this.base);
      for(let attempt=0;attempt<3;attempt++) {
        const timer=new AbortController(); const timeout=setTimeout(()=>timer.abort(),30000);
        const abort=()=>timer.abort(); this.abortController.signal.addEventListener('abort',abort,{once:true});
        let r;
        try { r=await fetch(u.href,{method:'GET',credentials:'same-origin',cache:'no-store',signal:timer.signal}); }
        catch(e) { if(this.abortController.signal.aborted) throw e; if(attempt===2)throw new DataError('Die Produktdaten konnten nicht vollständig geladen werden. Bitte erneut versuchen.','NETWORK'); }
        finally { clearTimeout(timeout);this.abortController.signal.removeEventListener('abort',abort); }
        if(r?.ok) return parseHTML(await r.text(),selector);
        if(r && r.status!==429 && r.status<500)throw new DataError('Datenansicht nicht erreichbar (HTTP '+r.status+').','HTTP');
        await new Promise(resolve=>setTimeout(resolve,400*(attempt+1)));
      }
      throw new DataError('Die Datenansicht antwortet zurzeit nicht.','NETWORK');
    }
    async loadCollection(path,onProgress=()=>{}) {
      const key=new URL(path,this.base).pathname;
      if(this.cache.has(key))return this.cache.get(key);
      const job=this._collection(key,onProgress);this.cache.set(key,job);
      try{return await job;}catch(e){this.cache.delete(key);throw e;}
    }
    async _collection(path,onProgress) {
      const firstURL=this.contextURL(path); firstURL.searchParams.set('section_id','pva-catalog');firstURL.searchParams.set('page','1');
      const first=await this.fetchPage(firstURL.href,'script[data-pva-catalog-page]');
      assert(first.schema==='pva-native-v1','Die Datenansicht hat eine andere Version.','SCHEMA');
      assert(Number.isInteger(first.total)&&first.total>=0&&first.total<=25000,'Kataloggröße nicht vollständig darstellbar.','LIMIT');
      assert(first.country===this.config.country&&first.currency===this.config.currency,'Land oder Währung haben sich geändert. Bitte die Seite neu laden.','CONTEXT');
      const pages=Math.max(1,first.pages);
      assert(Number.isInteger(pages)&&pages<=1250,'Ungültige Seitenzahl.');
      const firstSignature=JSON.stringify(first.products);
      const rows=[],seenIds=new Set();
      const add=(data,n)=>{
        assert(data.schema===first.schema&&data.collection===first.collection&&data.page===n&&data.total===first.total&&Math.max(1,data.pages)===pages,'Der Katalog wurde während des Ladens geändert. Bitte erneut laden.','DRIFT');
        assert(data.country===first.country&&data.currency===first.currency,'Inkonsistenter Preis-/Länderkontext.','CONTEXT');
        assert(Array.isArray(data.products),'Produktliste fehlt.');
        for(const p of data.products) { assert(p&&p.id&&p.handle&&!seenIds.has(p.id),'Doppelte oder unvollständige Katalogeinträge.','DRIFT');seenIds.add(p.id);rows.push(p); }
      };
      add(first,1);onProgress({loaded:rows.length,total:first.total});
      const other=await pool(Array.from({length:pages-1},(_,i)=>async()=>{
        const n=i+2,u=this.contextURL(path);u.searchParams.set('section_id','pva-catalog');u.searchParams.set('page',String(n));
        return {n,data:await this.fetchPage(u.href,'script[data-pva-catalog-page]')};
      }),3);
      other.sort((a,b)=>a.n-b.n).forEach(({n,data})=>{add(data,n);onProgress({loaded:rows.length,total:first.total});});
      assert(rows.length===first.total,'Nicht alle Produkte der Collection wurden geladen.');
      await pool(rows.map(p=>async()=>{
        assert(Number.isInteger(p.variantCount)&&p.variantCount>0&&Array.isArray(p.variants),'Variantenübersicht fehlt.');
        if(p.variants.length!==p.variantCount) p.variants=await this._variants(p);
        const unique=new Set(p.variants.map(v=>v.id));
        assert(unique.size===p.variantCount&&p.variants.length===p.variantCount&&!unique.has(null)&&!unique.has(undefined),'Varianten sind nicht vollständig.');
      }),3);
      // Verify index boundaries again. This detects common concurrent import drift;
      // Shopify offers no atomic snapshot across these separate native requests.
      const verify=await this.fetchPage(firstURL.href,'script[data-pva-catalog-page]');
      assert(verify.total===first.total&&verify.currency===first.currency&&verify.country===first.country&&JSON.stringify(verify.products)===firstSignature,'Der Shopbestand hat sich während des Ladens geändert. Bitte erneut versuchen.','DRIFT');
      return Object.freeze({schema:'pva-catalog-v1',collection:first.collection,products:rows,filters:first.filters||[],complete:true,country:first.country,currency:first.currency,loadedAt:Date.now(),atomic:false});
    }
    async _variants(p) {
      const result=[],seen=new Set();let next=this.contextURL(p.url),page=1;
      next.searchParams.set('view','pva-variants');next.searchParams.set('page','1');
      while(next) {
        const data=await this.fetchPage(next.href,'script[data-pva-variants-page]');
        assert(data.schema==='pva-variants-v1'&&data.id===p.id&&data.page===page&&data.variantCount===p.variantCount&&data.total===p.variantCount,'Die Varianten sind nicht vollständig zugänglich. Es wird keine Teilmenge empfohlen.','VARIANT_LIMIT');
        assert(Array.isArray(data.variants),'Variantenliste fehlt.');
        for(const v of data.variants){assert(v?.id&&!seen.has(v.id),'Doppelte Variante.');seen.add(v.id);result.push(v);}
        if(data.next){assert(page<50,'Varianten-Pagination überschreitet die unterstützte Grenze.','LIMIT');next=this.contextURL(data.next);next.searchParams.set('view','pva-variants');page++;}
        else next=null;
      }
      assert(result.length===p.variantCount,'Varianten unvollständig.');return result;
    }
  }
  global.PVACatalogLoader=CatalogLoader;
  global.PVADataError=DataError;
})(window);
